<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div class="row align-items-center mb-4">
        <div class="col-sm-6">
          <h6 class="fw-medium d-inline-flex align-items-center mb-3 mb-sm-0">
            <router-link :to="{ name: 'crm.contacts.list' }">
              <i class="ti ti-arrow-left me-2"></i>Contacts
            </router-link>
            <span class="text-gray d-inline-flex ms-2">/ {{ contact?.name || "—" }}</span>
          </h6>
        </div>
        <div class="col-sm-6">
          <div class="d-flex align-items-center justify-content-sm-end">
            <a
              v-if="contact?.email"
              :href="`mailto:${contact.email}`"
              class="btn btn-dark d-inline-flex align-items-center"
            >
              <i class="ti ti-mail me-2"></i>Send Email
            </a>
            <div class="head-icons ms-2 mb-0">
              <a href="javascript:void(0);" id="collapse-header" @click="toggleHeader">
                <i class="ti ti-chevrons-up"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading && !contact" class="card border-0">
        <div class="card-body text-muted">Loading contact...</div>
      </div>
      <div v-else-if="!contact" class="card border-0">
        <div class="card-body text-danger">Contact not found.</div>
      </div>

      <div v-else class="row">
        <div class="col-xl-4 theiaStickySidebar">
          <div class="card card-bg-1">
            <div class="card-body p-0">
              <span class="avatar avatar-xl avatar-rounded border border-2 border-white m-auto d-flex mb-2 bg-light align-items-center justify-content-center">
                <span class="fw-semibold fs-4">{{ initials(contact.name) }}</span>
              </span>

              <div class="text-center px-3 pb-3 border-bottom">
                <h5 class="d-flex align-items-center justify-content-center mb-1">
                  {{ contact.name }}
                  <i v-if="contact.status === 'Active'" class="ti ti-discount-check-filled text-success ms-1"></i>
                </h5>
                <p class="text-dark mb-1">{{ contact.client?.name || contact.company_name || "—" }}</p>
                <span v-if="contact.job_title" class="badge bg-pink-transparent">{{ contact.job_title }}</span>
              </div>

              <div class="p-3 border-bottom">
                <h6 class="mb-2">Basic Information</h6>
                <kv-row label="Phone">{{ contact.phone || "—" }}</kv-row>
                <kv-row label="Alt Phone">{{ contact.phone_alt || "—" }}</kv-row>
                <kv-row label="Email">{{ contact.email || "—" }}</kv-row>
                <kv-row label="Birthday">{{ formatDate(contact.birthday, "date") }}</kv-row>
                <kv-row label="Address">{{ fullAddress || "—" }}</kv-row>
              </div>

              <div class="p-3 border-bottom">
                <h6 class="mb-2">Other Information</h6>
                <kv-row label="Status">{{ contact.status || "—" }}</kv-row>
                <kv-row label="Source">{{ contact.source || "—" }}</kv-row>
                <kv-row label="Rating">{{ contact.rating || "—" }}</kv-row>
                <kv-row label="Last Modified">{{ formatDate(contact.updated_at) }}</kv-row>
              </div>

              <div class="p-3 border-bottom">
                <h6 class="mb-2">Tags</h6>
                <div class="d-flex align-items-center flex-wrap gap-2">
                  <span v-for="tag in contact.tags || []" :key="tag" class="badge" :class="tagClass(tag)">{{ tag }}</span>
                  <span v-if="!contact.tags?.length" class="text-muted">—</span>
                </div>
              </div>

              <div class="p-3 border-bottom">
                <h6 class="mb-2">Account</h6>
                <div class="d-flex align-items-center">
                  <span class="avatar avatar-md border rounded-circle">
                    <span class="avatar-title">{{ initials(contact.client?.name || contact.company_name) }}</span>
                  </span>
                  <div class="ms-2">
                    <div class="fw-medium">{{ contact.client?.name || contact.company_name || "—" }}</div>
                    <small class="text-muted">{{ companyDomain }}</small>
                  </div>
                </div>
              </div>

              <div class="p-3">
                <h6 class="mb-2">Social Links</h6>
                <div v-if="socialLinks.length" class="d-flex align-items-center flex-wrap gap-2">
                  <a
                    v-for="item in socialLinks"
                    :key="item.label"
                    :href="item.url"
                    target="_blank"
                    rel="noopener"
                    class="btn btn-icon btn-sm border rounded-circle"
                    :title="item.label"
                  >
                    <img :src="item.icon" :alt="item.label" class="img-fluid" />
                  </a>
                </div>
                <p v-else class="text-muted mb-0">No social links added.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-8">
          <crm-engagement-panel
            entity-type="contact"
            :entity-id="contact.id"
            :default-email="contact.email || ''"
            note-placeholder="Add an internal note about this contact"
          />
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a></p>
    </div>
  </div>
</template>

<script setup>
import { computed, defineComponent, h, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import CrmEngagementPanel from "@/components/crm/CrmEngagementPanel.vue";
import { useContactStore } from "@/stores/contactStore";

const route = useRoute();
const store = useContactStore();

const id = computed(() => {
  const value = Number(route.params.id);
  return Number.isFinite(value) ? value : null;
});

const loading = computed(() => store.loading);
const contact = computed(() => (id.value != null ? store.byId?.[id.value] : null));
const fullAddress = computed(() =>
  [contact.value?.address, contact.value?.city, contact.value?.state, contact.value?.country, contact.value?.zipcode]
    .filter(Boolean)
    .join(", ")
);
const companyDomain = computed(() => {
  const website = contact.value?.client?.website;
  if (!website) return "No website";
  return website.replace(/^https?:\/\//i, "").replace(/\/$/, "");
});
const socialLinks = computed(() =>
  [
    { label: "Facebook", url: contact.value?.facebook, icon: require("@/assets/img/social/social-01.svg") },
    { label: "Twitter", url: contact.value?.twitter, icon: require("@/assets/img/social/social-02.svg") },
    { label: "LinkedIn", url: contact.value?.linkedin, icon: require("@/assets/img/social/social-03.svg") },
    { label: "Instagram", url: contact.value?.instagram, icon: require("@/assets/img/social/social-04.svg") },
    { label: "WhatsApp", url: contact.value?.whatsapp, icon: require("@/assets/img/social/social-05.svg") },
    { label: "Skype", url: contact.value?.skype, icon: require("@/assets/img/social/social-06.svg") },
  ].filter((item) => item.url)
);

async function loadContact(targetId) {
  if (!targetId) return;
  if (!store.byId?.[targetId]) {
    try {
      await store.ensureOne(targetId);
    } catch {}
  }
}

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function initials(value = "") {
  return String(value)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "CT";
}

function formatDate(value, mode = "datetime") {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return mode === "date" ? date.toISOString().slice(0, 10) : date.toLocaleString();
}

function tagClass(tag = "") {
  const normalized = tag.toLowerCase();
  if (normalized.includes("vip")) return "badge-soft-warning";
  if (normalized.includes("active")) return "badge-soft-success";
  return "badge-dark-transparent";
}

const KvRow = defineComponent({
  name: "KvRow",
  props: { label: { type: String, default: "" } },
  setup(props, { slots }) {
    return () =>
      h("div", { class: "d-flex align-items-center justify-content-between mb-2" }, [
        h("span", { class: "d-inline-flex align-items-center" }, [h("i", { class: "ti ti-dots me-2" }), props.label]),
        h("p", { class: "text-dark mb-0 text-end" }, slots.default ? slots.default() : "—"),
      ]);
  },
});

onMounted(async () => {
  await loadContact(id.value);
});

watch(id, async (value, previous) => {
  if (value && value !== previous) {
    await loadContact(value);
  }
});
</script>

<style scoped>
@media (max-width: 767.98px) {
  .row.align-items-center.mb-4 > [class*="col-"] {
    width: 100%;
  }

  .row.align-items-center.mb-4 .d-flex.align-items-center.justify-content-sm-end {
    justify-content: flex-start !important;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .card-body.p-0 {
    overflow: hidden;
  }
}
</style>


